/**
 * one-stop/primitives — the shared visual atoms, lifted from the prototype.
 *
 * Each one is a piece of `Lakeview OneStop.dc.html` given a name: the section
 * head with its H2 + subtitle + right-hand note, the standing white card, the
 * date tile on an event row, the CTA cluster that goes auto-width on desktop and
 * splits full-width on mobile, the green confirmation panel. Blocks compose
 * these, so a change to the Soll is made once.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { JSX, ReactNode } from "react";
import { Button, Input } from "@v0y4ge/jrni-ui";
import type {
  PortalAppointmentsFragment,
  PortalBusyLevel,
  PortalChecklistFragment,
  PortalDirectoryFragment,
  PortalEventsFragment,
  PortalIdentityFragment,
  PortalQueuesFragment,
} from "@v0y4ge/api-contracts";
import type { CtaState, VisitorDetails, VisitorFieldName } from "./actions";
import {
  VISITOR_FIELDS,
  refusalMessages,
  validateVisitor,
  visitorFieldMessages,
  visitorFormMessages,
} from "./actions";
import { splitDisplayName, useOneStopSession, useVisitorMemory } from "./session";
import type { OneStopSession } from "./session";
import type { OneStopBlock } from "./types";
import type { StringKey, Translate } from "./strings";

/* ── unknown reporting ─────────────────────────────────────── */

const reported = new Set<string>();

/**
 * Report an unknown block type or fragment kind ONCE per identity, then carry on.
 * A published document outlives any one client, so an unknown type is an expected
 * event, not an error — it must never throw and never blank the page.
 */
export function reportUnknown(what: "block" | "fragment", detail: Record<string, unknown>): void {
  const key = `${what}:${JSON.stringify(detail)}`;
  if (reported.has(key)) return;
  reported.add(key);
  console.warn(`[one-stop] skipped unknown ${what}`, detail);
}

/* ── fragment narrowing ─────────────────────────────────── */

type KnownFragment =
  | PortalQueuesFragment
  | PortalAppointmentsFragment
  | PortalEventsFragment
  | PortalDirectoryFragment
  | PortalIdentityFragment
  | PortalChecklistFragment;

/**
 * Read `block.data` only when it is the fragment kind this block type expects.
 * An unknown or mismatched kind is reported and treated as absent — the block
 * then takes its empty branch, which is the honest render.
 */
export function fragmentOf<K extends KnownFragment["kind"]>(
  block: OneStopBlock,
  kind: K,
): Extract<KnownFragment, { kind: K }> | null {
  const data = block.data;
  if (data === undefined || data === null) return null;
  const actual = (data as { kind?: unknown }).kind;
  if (actual !== kind) {
    reportUnknown("fragment", { block: block.id, expected: kind, received: actual });
    return null;
  }
  return data as Extract<KnownFragment, { kind: K }>;
}

/* ── tone maps ──────────────────────────────────────── */

/**
 * ui-config — the prototype's wait colours, against the CONTRACT's busy
 * vocabulary. The classification itself is computed server-side from the block's
 * configured thresholds, so the client never re-derives which word applies; this
 * map only says what colour each word wears.
 */
export const BUSY_TONE: Record<PortalBusyLevel, string> = {
  "short-wait": "var(--jrni-color-success-fill)",
  busy: "var(--jrni-color-warning-fill)",
  "very-busy": "var(--jrni-color-danger-fill)",
  closed: "var(--jrni-color-text-soft)",
};

/** ui-config — the same three tones over the registration vocabulary. */
export const REGISTRATION_TONE: Record<string, string> = {
  open: "var(--jrni-color-success-fill)",
  "not-required": "var(--jrni-color-success-fill)",
  waitlist: "var(--jrni-color-warning-fill)",
  full: "var(--jrni-color-danger-fill)",
  closed: "var(--jrni-color-text-soft)",
};

/* ── layout atoms ───────────────────────────────── */

export function Section(props: {
  title?: string | null;
  subtitle?: string | null;
  /** The right-hand line of the head row ("Shortest wait first", "All events"). */
  note?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  const hasHead = Boolean(props.title || props.subtitle || props.note);
  return (
    <section className="lv-section">
      {hasHead ? (
        <div className="lv-section-head">
          <div className="lv-section-headings">
            {props.title ? <h2 className="lv-h2">{props.title}</h2> : null}
            {props.subtitle ? <p className="lv-sub">{props.subtitle}</p> : null}
          </div>
          {props.note ? <span className="lv-note">{props.note}</span> : null}
        </div>
      ) : null}
      {props.children}
    </section>
  );
}

/**
 * The empty state. Deliberately "not available right now" rather than "there are
 * none": a present-and-empty fragment means the source is unreachable OR
 * genuinely empty, and the two are different facts. Overclaiming here is how a
 * live page silently tells a student no desks are open.
 */
export function Empty({ children }: { children: ReactNode }): JSX.Element {
  return <p className="lv-empty">{children}</p>;
}

export function DateTile({ mon, day }: { mon: string; day: string }): JSX.Element {
  return (
    <span className="lv-datetile" aria-hidden="true">
      <span className="lv-datetile-mon">{mon}</span>
      <span className="lv-datetile-day">{day}</span>
    </span>
  );
}

/* ── CTA atoms ──────────────────────────────── */

/** The cluster: auto-width buttons on desktop, split full-width at 640px. */
export function Actions({ children }: { children: ReactNode }): JSX.Element {
  return <div className="lv-actions">{children}</div>;
}

export function Act({ children }: { children: ReactNode }): JSX.Element {
  return <span className="lv-act">{children}</span>;
}

/**
 * One CTA. Renders in every state the docket asks for:
 *  - available and idle → a live button
 *  - running            → the button, disabled, saying so
 *  - unavailable        → the button, disabled, with `reason` visible beneath
 *
 * Never hidden and never silently dead: a control a student can see but not use
 * has to say why, and one that does nothing at all is worse than one that is
 * evidently not ready.
 *
 * ⛔ `disabledReason` IS FOR "NOTHING MAY BE CALLED HERE", NEVER FOR "THE FORM IS
 * NOT FILLED IN YET". A submit button greyed out because a field is empty tells
 * the visitor nothing about WHICH field, cannot be tapped to find out, and on a
 * touch device has no tooltip to fall back on. An incomplete form now keeps its
 * button live: pressing it marks the offending fields, focuses the first, and
 * sends nothing.
 */
export function CtaButton(props: {
  label: string;
  onClick: () => void;
  t: Translate;
  hierarchy?: "primary" | "secondary";
  size?: "small" | "medium";
  /** null when the action is available. */
  disabledReason: string | null;
  running?: boolean;
}): JSX.Element {
  const blocked = props.disabledReason !== null;
  return (
    <Act>
      <Button
        hierarchy={props.hierarchy ?? "primary"}
        size={props.size ?? "small"}
        disabled={blocked || props.running === true}
        onClick={props.onClick}
        title={props.disabledReason ?? undefined}
      >
        {props.running ? props.t("cta.working") : props.label}
      </Button>
    </Act>
  );
}

/**
 * The line beneath a CTA: the disabled reason, the failure in the page's own
 * words, or nothing.
 *
 * FAILURES THAT ARE NORMAL OUTCOMES GET THEIR OWN SENTENCE. `SLOT_UNAVAILABLE`
 * means someone took the slot while the visitor was deciding; `HOLD_EXPIRED`
 * means the five minutes ran out; `QUEUE_NOT_OPEN` means the desk closed. None
 * of those is "that didn't go through" — they are answers, and the copy says
 * what happened. `HOLD_INVALID` is the careful one: a Confirm that looked like
 * it failed may in fact have succeeded, so the copy points at the inbox instead
 * of inviting a second submission (kit §6).
 *
 * `source: "network"` is a different sentence again: the server never answered,
 * as against answering no. A visitor can tell the difference and the copy
 * shouldn't pretend otherwise.
 *
 * ⛔ THE ENVELOPE'S OWN `message` IS NEVER PRINTED HERE. It used to be appended
 * to the generic sentence, which is how a student typing `pat` into Email was
 * answered with "That didn't go through. Invalid request body" — an internal
 * English string, untranslated by construction, naming nothing they could fix.
 *
 * WHAT IS PRINTED, IN ORDER: a coded outcome's own copy; the offline sentence;
 * the refusal's FORM-LEVEL messages, which are the ones that named a group or
 * named nothing (`checkout` keys its refusals by the `customer` object, so this
 * is where a checkout validation failure is always legible, whatever field it
 * was really about); nothing at all when the refusal named FIELDS, because those
 * are answered under the fields themselves; and otherwise ONE plain catalogue
 * sentence — the same one in every form, per locale, never the server's words.
 *
 * A `<span>`, not a `<p>`, on purpose: several rows ARE the CTA (the whole
 * appointment row is the Book button), so this element renders inside a
 * `<button>`, where a `<p>` would be invalid HTML. It is blocked out with an
 * inline `display` rather than a class so that stays true wherever it is used.
 */
const CODE_COPY: Record<string, StringKey> = {
  SLOT_UNAVAILABLE: "book.taken",
  HOLD_EXPIRED: "book.expired",
  HOLD_INVALID: "book.maybeBooked",
  QUEUE_NOT_OPEN: "queue.closed",
};

function errorCopy(t: Translate, state: CtaState<unknown>): string | null {
  if (state.status !== "error") return null;
  const key = state.code ? CODE_COPY[state.code] : undefined;
  if (key) return t(key);
  if (state.source === "network") return t("cta.offline");
  const form = visitorFormMessages(state.refusal);
  if (form.length > 0) return form.join(" ");
  // Already answered at the fields — a second, vaguer sentence is noise.
  if (Object.keys(visitorFieldMessages(state.refusal.fields)).length > 0) return null;
  return t("cta.error");
}

export function CtaFeedback(props: {
  t: Translate;
  disabledReason: string | null;
  state: CtaState<unknown>;
}): JSX.Element | null {
  if (props.state.status === "error") {
    const copy = errorCopy(props.t, props.state);
    if (copy === null) return null;
    return (
      <span className="lv-cta-error" style={{ display: "block" }} role="status">
        {copy}
      </span>
    );
  }
  if (props.disabledReason) {
    return (
      <span className="lv-cta-reason" style={{ display: "block" }}>
        {props.disabledReason}
      </span>
    );
  }
  return null;
}

/* ── a reference, and what a visitor can do with it ────────── */

/** How long "Copied" stands after a successful write. */
const COPIED_MS = 2000;

/**
 * A resolved sentence, split around the value interpolated into it.
 *
 * The catalog owns the sentence ("Ticket {ref}", "Turno {ref}"), so the only way
 * to give the VALUE its own element without a second key per locale is to find it
 * in the resolved string. `null` when it is not there — an empty reference, or a
 * locale whose sentence dropped the placeholder — and the caller then renders the
 * sentence whole rather than inventing markup around nothing.
 */
function splitAround(sentence: string, value: string): readonly [string, string] | null {
  if (value === "") return null;
  const at = sentence.indexOf(value);
  if (at < 0) return null;
  return [sentence.slice(0, at), sentence.slice(at + value.length)];
}

/**
 * A sentence whose value is selectable AS ONE UNIT.
 *
 * A ticket id or a check-in code is quoted at a desk and typed into another
 * device, so what matters is that one tap or double-click takes the WHOLE code
 * and not the word beside it or one hyphen-delimited half of it. That is
 * `user-select: all` on the value's own element, which is why the value needs an
 * element at all.
 */
export function SelectableValue(props: { sentence: string; value: string }): JSX.Element {
  const parts = splitAround(props.sentence, props.value);
  if (parts === null) return <>{props.sentence}</>;
  return (
    <>
      {parts[0]}
      <span className="lv-code">{props.value}</span>
      {parts[1]}
    </>
  );
}

/**
 * The appointment confirmation's reference: a LINK when the checkout handed back
 * a manage URL for that booking, and the same plain text as before when it did
 * not.
 *
 * NEVER A DEAD ANCHOR, and that is the whole reason this is one component rather
 * than a conditional at each call site. The field is absent on every deployment
 * today (JAI-1189), so the unlinked branch is the one that actually renders; it
 * has to look finished, not like a link that failed. Same sentence, same key,
 * same geometry — one element gains an `href`.
 *
 * THE VISIBLE TEXT IS THE REFERENCE. The URL is never rendered, never captioned,
 * never offered as a "copy link" — a photograph of this screen in a branch is
 * harmless with a reference in it and is a bearer credential with the URL in it.
 *
 * NEW TAB (CC 0053 §2), because the One-Stop page is where the visitor just did
 * something and may still have something to do on it — another desk, another
 * appointment, the queue they are in. `rel` is unchanged and deliberately so:
 * `noopener` is now load-bearing rather than belt-and-braces, `nofollow` states
 * the intent on a public page (0026 §3), and `noreferrer` is NOT added — the
 * manage surface serves `referrer-policy: no-referrer` itself, so it would be a
 * second spelling of a rule the server already enforces.
 * The accessible name comes from the catalog: the reference alone says what the
 * link IS and not where it goes.
 *
 * The link style is the renderer's own — `.lv-root a` in the sheet — so this
 * reads exactly like every other link on the page and invents no variant.
 */
export function ManagedReference(props: {
  t: Translate;
  reference: string;
  manageUrl: string | null;
}): JSX.Element {
  const sentence = props.t("ref", { ref: props.reference });
  if (props.manageUrl === null) return <>{sentence}</>;
  const parts = splitAround(sentence, props.reference);
  const label = props.t("ref.manage.aria", { ref: props.reference });
  // No placeholder to wrap: link the sentence rather than lose the affordance.
  if (parts === null) {
    return (
      <a href={props.manageUrl} target="_blank" rel="noopener nofollow" aria-label={label}>
        {sentence}
      </a>
    );
  }
  return (
    <>
      {parts[0]}
      <a href={props.manageUrl} target="_blank" rel="noopener nofollow" aria-label={label}>
        {props.reference}
      </a>
      {parts[1]}
    </>
  );
}

/**
 * Copy one value to the clipboard, in the page's own words.
 *
 * NOT RENDERED AT ALL WHERE THERE IS NO CLIPBOARD. `navigator.clipboard` is
 * undefined outside a secure context, and a Copy button that silently does
 * nothing is worse than no button — the value is selectable either way, which is
 * the honest fallback. The check runs after mount so the first paint is the same
 * on the server and the client.
 *
 * THE CONFIRMATION IS ITS OWN LIVE REGION, not a swapped button label. The
 * button's accessible name says WHICH value it copies ("Copy check-in code
 * 4821"), and a name that changed to "Copied" would stop saying that; a
 * `role="status"` sibling announces the write, shows it, and stands down.
 */
export function CopyAction(props: {
  t: Translate;
  /** The exact characters to put on the clipboard — the code, never a sentence. */
  value: string;
  /** The accessible name, from the catalog, naming what is copied. */
  label: string;
}): JSX.Element | null {
  const [available, setAvailable] = useState(false);
  const [done, setDone] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    setAvailable(
      typeof navigator !== "undefined" && typeof navigator.clipboard?.writeText === "function",
    );
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, []);

  const value = props.value;
  const copy = useCallback(() => {
    if (typeof navigator.clipboard?.writeText !== "function") return;
    void navigator.clipboard.writeText(value).then(
      () => {
        setDone(true);
        if (timer.current !== null) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setDone(false), COPIED_MS);
      },
      // A refused clipboard is not something to explain to a visitor who can
      // still select the code with their finger.
      () => undefined,
    );
  }, [value]);

  if (!available || value === "") return null;

  return (
    <span className="lv-copyrow">
      <Button variant="ghost" size="small" onClick={copy} aria-label={props.label}>
        {props.t("copy.action")}
      </Button>
      <span className="lv-copied" role="status">
        {done ? props.t("copy.done") : ""}
      </span>
    </span>
  );
}

/* ── the details every flow requires ──────────────────────── */

/**
 * What goes under one field: a CATALOG KEY when it is our own rule (so the page
 * says it in the page's language) or the server's own words when the refusal
 * came from the server. Two sources, one slot, and the server wins — our rules
 * already passed by the time a request went out.
 */
export type VisitorFieldMessage = { readonly key: StringKey } | { readonly text: string };

type ServerMessages = Readonly<Partial<Record<VisitorFieldName, string>>>;

export interface VisitorForm {
  readonly value: VisitorDetails;
  /** Nothing our own rules object to. NOT a gate on the submit button. */
  readonly complete: boolean;
  /** Whether the operation behind this form requires an email address. */
  readonly requireEmail: boolean;
  set(field: VisitorFieldName, next: string): void;
  /** What to show under each field right now. Empty until the visitor commits. */
  readonly messages: Readonly<Partial<Record<VisitorFieldName, VisitorFieldMessage>>>;
  /** Ref callback for a field's input, so the first invalid one can be focused. */
  bind(field: VisitorFieldName): (el: HTMLInputElement | null) => void;
  /**
   * Commit one field to the remembered record, if it is worth remembering. The
   * fields call this on BLUR, so the next form starts filled in whether or not
   * this one was ever submitted.
   */
  blur(field: VisitorFieldName): void;
  /**
   * The pre-flight, and the only thing standing between a tap and a request.
   * Marks every field our rules object to, focuses the first, and answers
   * `false` — the caller then makes NO call.
   */
  check(): boolean;
  /** Bind a refusal's field map onto the fields. */
  showServerErrors(err: unknown): void;
}

const EMPTY_VISITOR: VisitorDetails = { firstName: "", lastName: "", email: "", phone: "" };
const NO_SERVER_MESSAGES: ServerMessages = {};

/**
 * What a SIGNED-IN visitor — or one who already filled a form in this tab — should
 * not have to retype.
 *
 * THREE SOURCES, IN ONE ORDER, and the order is the whole design:
 *
 *   1. `identity.contact` — what the PLATFORM holds for a signed-in student
 *      (0019 §d). Authoritative, read, never derived.
 *   2. The REMEMBERED record — what this visitor typed into an earlier form in
 *      this tab. It beats the display-name split below it on purpose: the split is
 *      a heuristic on one unstructured string, while the record is what the
 *      visitor themself last committed, corrections included.
 *   3. The display-name split — names only, and only when nothing better exists.
 *      Email and phone are never guessed from it; there is nothing on that wire to
 *      guess them from, and inventing an address is worse than asking for one.
 *
 * THE TWO NAMES RESOLVE AS A PAIR. They are one wire string split in two, so a
 * first name from the record beside a family name from the split is a person who
 * does not exist. If the record carries EITHER name it supplies BOTH.
 *
 * Every field stays EDITABLE whichever source filled it: a prefill is a
 * convenience, never a lock.
 */
function seededVisitor(
  session: OneStopSession | null,
  remembered: VisitorDetails | null,
): VisitorDetails {
  const contact = session?.contact ?? null;
  const stored = remembered ?? EMPTY_VISITOR;
  const split = splitDisplayName(session?.displayName ?? null);
  const names = stored.firstName !== "" || stored.lastName !== "" ? stored : split;
  return {
    firstName: contact?.givenName ?? names.firstName,
    lastName: contact?.familyName ?? names.lastName,
    email: contact?.email ?? stored.email,
    phone: contact?.phone ?? stored.phone,
  };
}

/** The same map without one field. Rebuilt rather than mutated — the state is readonly. */
function withoutField(current: ServerMessages, drop: VisitorFieldName): ServerMessages {
  const next: Partial<Record<VisitorFieldName, string>> = {};
  for (const field of VISITOR_FIELDS) {
    const message = current[field];
    if (field !== drop && message !== undefined) next[field] = message;
  }
  return next;
}

/**
 * One CTA's details, in that CTA's own component state.
 *
 * Not lifted to the page and not persisted: two desks on one page are two
 * independent joins, and a shared draft would leak one card's half-typed email
 * into another. The same reason the entry token is not a singleton.
 *
 * `requireEmail` is which OPERATION is behind the form, not a preference: the
 * queue join's `email` is optional on the contract, `checkout` and the event
 * registration both require it. Default `true`, because two of the three do.
 *
 * WHEN A FIELD IS ALLOWED TO SAY IT IS WRONG. Never while it is being typed —
 * `marked` turns on at the first submit attempt (or the first refusal) and not
 * before, so a visitor three characters into their address is not told their
 * address is invalid. After that the marking is live, so a correction clears
 * itself as it is made.
 *
 * THE FORM HOLDS THE VISITOR'S EDITS, NOT A COPY OF THE PREFILL — and that is the
 * fix for 0024. It used to hold the whole record in state, seeded once at init and
 * patched by an effect that filled only fields that were still EMPTY. Two rules
 * that are each right collided: a signed-in form was initialised while the record
 * was still empty, so the display-name SPLIT seeded the names, and when the record
 * arrived "never overwrite a non-empty field" refused to correct them. A visitor
 * who fixed a mis-split name in one form saw the wrong split in every other form
 * on the page — the exact case the precedence exists for.
 *
 * So there is ONE resolver and no patching: `seededVisitor()` resolves
 * contact > record > split on EVERY render, `edits` holds only the fields this
 * visitor has typed into THIS form, and the rendered value is the resolver with
 * the edits over the top. A record that arrives late therefore wins the names it
 * outranks, and an edited field — including one deliberately cleared — is still
 * never overwritten, because an edit is recorded by its presence and not by being
 * non-empty.
 *
 * WHAT GOES INTO THE REMEMBERED RECORD, AND WHEN. Every field that is valid and
 * non-empty, on BLUR, and all four on a passing `check()` — the commit, which is
 * the moment the visitor asserted these details are theirs. It is deliberately
 * NOT the server's 200: a refusal about a taken slot or a closed desk says nothing
 * about whether their name is right, and a student who has to retype their address
 * because a desk closed is the defect this exists to remove. Invalid and empty
 * values are never written, so the record cannot be poisoned by a half-typed one.
 */
export function useVisitor(options?: { readonly requireEmail?: boolean }): VisitorForm {
  const requireEmail = options?.requireEmail !== false;
  const { session } = useOneStopSession();
  const memory = useVisitorMemory();
  /** ONLY what this visitor typed here. A key's PRESENCE is the edit, so a field
   *  cleared on purpose stays cleared instead of being re-prefilled. */
  const [edits, setEdits] = useState<Partial<VisitorDetails>>({});
  const [marked, setMarked] = useState(false);
  const [server, setServer] = useState<ServerMessages>(NO_SERVER_MESSAGES);
  const inputs = useRef<Partial<Record<VisitorFieldName, HTMLInputElement | null>>>({});
  const binders = useRef<Partial<Record<VisitorFieldName, (el: HTMLInputElement | null) => void>>>({});

  // The resolver, re-run every render: the session resolving, a contact fragment
  // arriving and another form on this page remembering a field all reach this form
  // through it, and none of them can overwrite an edit.
  const seed = seededVisitor(session, memory.details);
  const value = useMemo<VisitorDetails>(
    () => ({
      firstName: edits.firstName ?? seed.firstName,
      lastName: edits.lastName ?? seed.lastName,
      email: edits.email ?? seed.email,
      phone: edits.phone ?? seed.phone,
    }),
    [edits, seed.firstName, seed.lastName, seed.email, seed.phone],
  );

  const problems = validateVisitor(value, { requireEmail });
  const complete = Object.keys(problems).length === 0;

  const set = useCallback((field: VisitorFieldName, next: string) => {
    setEdits((current) => ({ ...current, [field]: next }));
    // The server's verdict was about the OLD value. Drop it the moment the field
    // changes, or a corrected address keeps its refusal sitting underneath it.
    setServer((current) => (current[field] === undefined ? current : withoutField(current, field)));
  }, []);

  // A STABLE ref callback per field. A fresh closure each render would make React
  // detach and re-attach the ref on every keystroke, which is how a focus() call
  // lands on an element that is no longer the one on screen.
  const bind = useCallback((field: VisitorFieldName) => {
    const existing = binders.current[field];
    if (existing) return existing;
    const setter = (el: HTMLInputElement | null): void => {
      inputs.current[field] = el;
    };
    binders.current[field] = setter;
    return setter;
  }, []);

  const remember = memory.remember;

  /**
   * One field, on blur. It is remembered only if it is valid ON ITS OWN — the
   * whole-form verdict is not the question here: a visitor who has typed a good
   * email and no name yet should still not have to retype that email on the next
   * form.
   */
  const blur = useCallback(
    (field: VisitorFieldName) => {
      const typed = value[field].trim();
      if (typed === "") return;
      if (validateVisitor(value, { requireEmail })[field] !== undefined) return;
      remember({ [field]: typed } as Partial<VisitorDetails>);
    },
    [value, requireEmail, remember],
  );

  const check = useCallback((): boolean => {
    setMarked(true);
    setServer(NO_SERVER_MESSAGES);
    const found = validateVisitor(value, { requireEmail });
    const first = VISITOR_FIELDS.find((field) => found[field] !== undefined);
    if (first === undefined) {
      // The commit: these details passed our rules and the visitor pressed the
      // button. Remember all four; the empty ones are ignored by `remember`.
      remember({
        firstName: value.firstName,
        lastName: value.lastName,
        email: value.email,
        phone: value.phone,
      });
      return true;
    }
    inputs.current[first]?.focus();
    return false;
  }, [value, requireEmail, remember]);

  const showServerErrors = useCallback((err: unknown) => {
    const mapped = visitorFieldMessages(refusalMessages(err).fields);
    setMarked(true);
    setServer(mapped);
    const first = VISITOR_FIELDS.find((field) => mapped[field] !== undefined);
    if (first !== undefined) inputs.current[first]?.focus();
  }, []);

  const messages: Partial<Record<VisitorFieldName, VisitorFieldMessage>> = {};
  for (const field of VISITOR_FIELDS) {
    const fromServer = server[field];
    if (fromServer !== undefined) {
      messages[field] = { text: fromServer };
      continue;
    }
    const key = marked ? problems[field] : undefined;
    if (key !== undefined) messages[field] = { key };
  }

  return { value, complete, requireEmail, set, messages, bind, blur, check, showServerErrors };
}

/**
 * The fields, and only the fields the operations REQUIRE: first name, last name
 * and email are required by `checkout` and by the event registration, and the
 * queue join needs a display name. Nothing is collected for its own sake.
 *
 * `phone` is offered on the queue join and says why it is worth giving: it is
 * the only thing that dedupes a double-tapped Join today (kit §6), so asking
 * for it is a correctness affordance, not a marketing one.
 *
 * EVERY FIELD CARRIES ITS OWN VERDICT. `Input`'s `error` prop is the design
 * system's one error signal — red border, red focus ring, message beneath — and
 * it is what both our rules and the server's refusals render through, so a
 * visitor never has to guess which of four inputs a "no" was about.
 *
 * `inputMode` alongside `type`: `type` decides validation and autofill, `inputMode`
 * decides which keyboard a phone raises, and the two are not the same lever.
 *
 * EVERY FIELD REMEMBERS ITSELF ON BLUR. Leaving a valid field commits it to the
 * tab's one visitor record, which is what makes the second and third form of a
 * visit open filled in. `Input`'s own focus/blur handling is untouched — `onBlur`
 * is passed through, so the border and the focus ring behave exactly as before.
 */
export function VisitorFields(props: {
  form: VisitorForm;
  t: Translate;
  /** Only the queue join has a reason to ask. */
  askPhone?: boolean;
  disabled?: boolean;
}): JSX.Element {
  const { form, t } = props;
  const disabled = props.disabled === true;
  const messageFor = (field: VisitorFieldName): string | null => {
    const found = form.messages[field];
    if (found === undefined) return null;
    return "key" in found ? t(found.key) : found.text;
  };
  // The nudge before the first attempt, and only then: once fields carry their
  // own messages, a summary line under them repeats what they already say.
  const hint =
    !form.complete && Object.keys(form.messages).length === 0
      ? t(form.requireEmail ? "visitor.incomplete" : "visitor.incompleteName")
      : null;

  return (
    <div className="lv-visitor">
      <span className="lv-visitor-legend">{t("visitor.legend")}</span>
      <Input
        label={t("visitor.firstName")}
        value={form.value.firstName}
        autoComplete="given-name"
        ref={form.bind("firstName")}
        error={messageFor("firstName")}
        disabled={disabled}
        onBlur={() => form.blur("firstName")}
        onChange={(event) => form.set("firstName", event.target.value)}
      />
      <Input
        label={t("visitor.lastName")}
        value={form.value.lastName}
        autoComplete="family-name"
        ref={form.bind("lastName")}
        error={messageFor("lastName")}
        disabled={disabled}
        onBlur={() => form.blur("lastName")}
        onChange={(event) => form.set("lastName", event.target.value)}
      />
      <div className={props.askPhone === true ? "lv-visitor-email" : "lv-visitor-email lv-visitor-wide"}>
        <Input
          type="email"
          inputMode="email"
          label={t("visitor.email")}
          hint={form.requireEmail ? undefined : t("visitor.optional")}
          value={form.value.email}
          autoComplete="email"
          ref={form.bind("email")}
          error={messageFor("email")}
          disabled={disabled}
          onBlur={() => form.blur("email")}
          onChange={(event) => form.set("email", event.target.value)}
        />
      </div>
      {props.askPhone === true ? (
        <div className="lv-visitor-phone">
          <Input
            type="tel"
            inputMode="tel"
            label={t("visitor.phone")}
            hint={t("visitor.phoneWhy")}
            value={form.value.phone}
            autoComplete="tel"
            ref={form.bind("phone")}
            error={messageFor("phone")}
            disabled={disabled}
            onBlur={() => form.blur("phone")}
            onChange={(event) => form.set("phone", event.target.value)}
          />
        </div>
      ) : null}
      {hint ? <span className="lv-visitor-hint">{hint}</span> : null}
    </div>
  );
}

/** The green confirmation panel, lifted from the prototype's join/book screens.
 *
 * `headline` and `meta` take a NODE rather than a string: a reference that has
 * become a link, or a code that has to be selectable on its own, is markup inside
 * an otherwise unchanged sentence. Passing a string still renders exactly as it
 * always did. */
export function SuccessPanel(props: {
  eyebrow: string;
  headline: ReactNode;
  meta?: ReactNode;
  aside?: { value: string; label: string } | null;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className="lv-success" role="status">
      <span style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
        <span className="lv-success-eyebrow">{props.eyebrow}</span>
        <span className="lv-success-head">{props.headline}</span>
        {props.meta ? <span className="lv-success-meta">{props.meta}</span> : null}
        {props.children}
      </span>
      {props.aside ? (
        <span
          style={{ flex: "none", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}
        >
          <span className="lv-bignum" style={{ color: "var(--jrni-color-status-success-text)" }}>
            {props.aside.value}
          </span>
          <span className="lv-success-meta">{props.aside.label}</span>
        </span>
      ) : null}
    </div>
  );
}
